import { format } from 'date-fns';
import { isFinite } from 'lodash';
import * as XLSX from 'xlsx';

export const PlannedVsActualReportExcelExport = (data, startDate, endDate) => {
  const addRowStyle = (worksheet, rowNum, color) => {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cellRef = XLSX.utils.encode_cell({ r: rowNum, c: col });
      if (!worksheet[cellRef]) {
        worksheet[cellRef] = { v: '', t: 's' };
      }
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      worksheet[cellRef].s.fill = {
        patternType: 'solid',
        fgColor: { rgb: color.replace('#', '') },
      };
    }
  };

  const secondsToHours = (seconds) => {
    seconds = Math.round(Number(seconds));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateProgress = (consumed, planned) => {
    if (!planned || planned === 0 || !consumed || consumed < 0) {
      return '0';
    }
    const numericProgress = parseFloat((consumed / planned) * 100);
    if (!Number(numericProgress) || numericProgress < 0 || !isFinite(numericProgress)) {
      return '0';
    }
    return `${Math.round(numericProgress)}`;
  };

  const workbook = XLSX.utils.book_new();

  // Project Level Sheet
  const projectLevelHeaders = [
    [`Duration: ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`],
    [''],
    ['Project Name', 'Project Status', 'Total Consumed (Hours)', 'Total Planned (Hours)', 'Progress %'],
  ];

  const projectLevelData = data.map((project) => ({
    'Project Name': project.projectName,
    'Project Status': project.status,
    'Total Consumed (Hours)': secondsToHours(project.totalConsumedSeconds),
    'Total Planned (Hours)': secondsToHours(project.totalPlannedSeconds),
    'Progress %': calculateProgress(project.totalConsumedSeconds, project.totalPlannedSeconds),
  }));

  const projectSheet = XLSX.utils.aoa_to_sheet(projectLevelHeaders);
  XLSX.utils.sheet_add_json(projectSheet, projectLevelData, { origin: 'A3' });
  XLSX.utils.book_append_sheet(workbook, projectSheet, 'Project Level');

  // Project Member Sheet
  const memberHeaders = [
    [`Duration: ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`],
    [''],
    [
      'Project Name',
      'User Name',
      'Task Name',
      'Task Status',
      'Task Type',
      'Task Category',
      'Planned Hours',
      'Consumed Hours',
      'Progress %',
    ],
  ];

  const memberSheet = XLSX.utils.aoa_to_sheet(memberHeaders);
  const memberLevelData = [];

  data.forEach((project) => {
    project.members.forEach((member) => {
      member.tasks.forEach((task) => {
        memberLevelData.push({
          'Project Name': project.projectName,
          'User Name': member.userName,
          'Task Name': task.taskName,
          'Task Status': task.status,
          'Task Type': task.taskType,
          'Task Category': task.isBillable ? 'Billable' : 'Non-Billable',
          'Planned Hours': secondsToHours(task.plannedSeconds),
          'Consumed Hours': secondsToHours(task.consumedSeconds),
          'Progress %': calculateProgress(task.consumedSeconds, task.plannedSeconds),
        });
      });

      memberLevelData.push({
        'Project Name': project.projectName,
        'User Name': member.userName,
        'Task Name': '--- MEMBER TOTAL ---',
        'Task Status': '',
        'Task Type': '',
        'Task Category': '',
        'Planned Hours': secondsToHours(member.totalPlannedSeconds),
        'Consumed Hours': secondsToHours(member.totalConsumedSeconds),
        'Progress %': calculateProgress(member.totalConsumedSeconds, member.totalPlannedSeconds),
      });
    });

    memberLevelData.push(
      {
        'Project Name': project.projectName,
        'User Name': '=== PROJECT TOTAL ===',
        'Task Name': '',
        'Task Status': '',
        'Task Type': '',
        'Task Category': '',
        'Planned Hours': secondsToHours(project.totalPlannedSeconds),
        'Consumed Hours': secondsToHours(project.totalConsumedSeconds),
        'Progress %': calculateProgress(project.totalConsumedSeconds, project.totalPlannedSeconds),
      },
      {
        'Project Name': '',
        'User Name': '',
        'Task Name': '',
        'Task Status': '',
        'Task Type': '',
        'Task Category': '',
        'Planned Hours': '',
        'Consumed Hours': '',
        'Progress %': '',
      }
    );
  });

  XLSX.utils.sheet_add_json(memberSheet, memberLevelData, { origin: 'A3' });

  // Add colors to member sheet
  memberLevelData.forEach((row, index) => {
    if (row['Task Name'] === '--- MEMBER TOTAL ---') {
      addRowStyle(memberSheet, index + 3, '#CDC1FF');
    } else if (row['User Name'] === '=== PROJECT TOTAL ===') {
      addRowStyle(memberSheet, index + 3, '#E3F2FD');
    }
  });

  XLSX.utils.book_append_sheet(workbook, memberSheet, 'Project Member');

  // Task Level Sheet
  const taskHeaders = [
    [`Duration: ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`],
    [''],
    [
      'Project Name',
      'Task Name',
      'User Name',
      'Task Type',
      'Task Status',
      'Task Category',
      'Planned Hours',
      'Consumed Hours',
      'Progress %',
    ],
  ];

  const taskSheet = XLSX.utils.aoa_to_sheet(taskHeaders);
  const taskLevelData = [];

  data.forEach((project) => {
    project.tasks.forEach((task) => {
      taskLevelData.push({
        'Project Name': project.projectName,
        'Task Name': task.taskName,
        'User Name': task.users.map((user) => user.userName).join(', '),
        'Task Type': task.taskType,
        'Task Status': task.status,
        'Task Category': task.isBillable ? 'Billable' : 'Non-Billable',
        'Planned Hours': secondsToHours(task.plannedSeconds),
        'Consumed Hours': secondsToHours(task.consumedSeconds),
        'Progress %': calculateProgress(task.consumedSeconds, task.plannedSeconds),
      });
    });

    taskLevelData.push(
      {
        'Project Name': project.projectName,
        'Task Name': '=== PROJECT TOTAL ===',
        'User Name': '',
        'Task Type': '',
        'Task Status': '',
        'Task Category': '',
        'Planned Hours': secondsToHours(project.totalPlannedSeconds),
        'Consumed Hours': secondsToHours(project.totalConsumedSeconds),
        'Progress %': calculateProgress(project.totalConsumedSeconds, project.totalPlannedSeconds),
      },
      {
        'Project Name': '',
        'Task Name': '',
        'User Name': '',
        'Task Type': '',
        'Task Status': '',
        'Task Category': '',
        'Planned Hours': '',
        'Consumed Hours': '',
        'Progress %': '',
      }
    );
  });

  XLSX.utils.sheet_add_json(taskSheet, taskLevelData, { origin: 'A3' });

  // Add colors to task sheet
  taskLevelData.forEach((row, index) => {
    if (row['Task Name'] === '=== PROJECT TOTAL ===') {
      addRowStyle(taskSheet, index + 3, '#B9E5E8');
    }
  });

  const taskColWidths = [
    { wch: 15 }, // Project Name
    { wch: 30 }, // Task Name
    { wch: 20 }, // User Name
    { wch: 12 }, // Task Type
    { wch: 12 }, // Task Status
    { wch: 12 }, // Task Category
    { wch: 12 }, // Planned Hours
    { wch: 12 }, // Consumed Hours
    { wch: 12 }, // Progress %
  ];
  taskSheet['!cols'] = taskColWidths;

  XLSX.utils.book_append_sheet(workbook, taskSheet, 'Task Level');

  const otherSheets = ['Project Level', 'Project Member'];
  otherSheets.forEach((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const columnWidths = [];
    Object.keys(worksheet).forEach((cell) => {
      if (cell[0] !== '!') {
        const column = cell.replace(/[0-9]/g, '');
        columnWidths[column] = Math.max(columnWidths[column] || 10, worksheet[cell].v?.toString().length || 10);
      }
    });
    worksheet['!cols'] = columnWidths.map((width) => ({ width: { wch: width } }));
  });

  XLSX.writeFile(workbook, 'Planned_vs_Actual_Report.xlsx');
};
